import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Divider, Link, Paper, Typography } from '@mui/material';
import { TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { BENEFIT_PICTURES } from 'probonio-shared-ui/component/button';
import { BikeleasingIcon } from 'probonio-shared-ui/component/icon';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useTranslation } from 'react-i18next';
import { BackIconButton } from '../../component/button/BackIconButton';
import { ExternalLinkButton } from '../../component/button/ExternalLinkButton';
import { MobileLayout } from '../../component/layout';
import { AppToolbar } from '../../component/layout/AppToolbar';
import { useBikeleasingDetails } from '../../module/bikeleasingV2/useBikeleasingDetails';
import { useBikeleasingHelpfulResourceLinks, useBikeleasingInformNowUrl } from '../../module/bikeleasingV2/useBikeleasingLinks';
import { useBackNavigator } from '../../module/navigation/BackNavigator';

interface TextProps {
  children: React.ReactNode;
}

const InfoText: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  return (
    <Paper data-test-id="bike-info-intro" elevation={0} sx={{ paddingY: 0.3, width: '100%', paddingRight: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="description">{t('extensions:bikeleasing_v2_benefit.infoPage.infoText')}</Typography>
      </Box>
    </Paper>
  );
};

const DesiredBike: React.FC = () => {
  const { tenant } = useTenant();
  const { t } = useTranslation('benefitModule');
  return (
    <Paper data-test-id="bike-info-desired-bike" elevation={0} sx={{ paddingY: 0.3, width: '100%', paddingRight: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="description">
          {t('extensions:bikeleasing_v2_benefit.infoPage.desiredBike', {
            discount: tenant?.countryCode === TenantCountryCodeEnum.At ? 44 : 40,
          })}
        </Typography>
      </Box>
    </Paper>
  );
};

export const ReceiptAdvantagesText: React.FC<TextProps> = ({ children }) => {
  return (
    <Box
      sx={{
        marginLeft: 0.3,
        marginRight: 0.5,
        marginBottom: 0.7,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <CheckIcon color="success" />
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
};

const ReceiptBenefits: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  const details = useBikeleasingDetails();

  return (
    <Paper data-test-id="bike-receipt-advantages" elevation={0} sx={{ paddingY: 0.3, width: '100%' }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="descriptionBold">{t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.name')}</Typography>
        <Divider variant={'fullWidth'} sx={{ marginRight: 0.3, marginBottom: 0.3, marginTop: 0.1 }} />
        <Typography
          variant={'description'}
          component="p"
          sx={{
            marginBottom: 0.7,
          }}
        >
          {t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.info')}
        </Typography>
        <ReceiptAdvantagesText>{t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.calculateCosts')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>
          {t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.bikeLeasing', {
            companyCode: details?.bikeleasingCondition.companyCode,
          })}
        </ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.retailPartners')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.bikePickUp')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('extensions:bikeleasing_v2_benefit.infoPage.howItWorks.leasingContract')}</ReceiptAdvantagesText>
        <Divider variant={'fullWidth'} sx={{ marginRight: 0.3, marginTop: 0.1 }} />
      </Box>
    </Paper>
  );
};

const HelpfulResources: React.FC = () => {
  const { t } = useTranslation('extensions');
  const { titleTranslationKey, links } = useBikeleasingHelpfulResourceLinks();

  return (
    <Paper data-test-id="bikeleasing-helpful-resources" elevation={0} sx={{ width: '100%', mb: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="descriptionBold">{t(titleTranslationKey)}:</Typography>
        {links.map(resourceLink => (
          <Link
            key={resourceLink.translationKey}
            href={resourceLink.url}
            target="_blank"
            rel="noopener"
            sx={{ display: 'flex', flexDirection: 'row', gap: 0.2, mt: 1, textDecoration: 'none' }}
          >
            <Typography variant="descriptionBold" color="primary">
              {t(`bikeleasing_v2.infoPage.resources.${resourceLink.translationKey}`)}
            </Typography>
            <ArrowCircleRightOutlinedIcon />
          </Link>
        ))}
      </Box>
    </Paper>
  );
};

export const BikeleasingV2InfoPage: React.FC = () => {
  const backNavigator = useBackNavigator();
  const { t } = useTranslation('benefitModule');
  const informNowLink = useBikeleasingInformNowUrl();

  return (
    <MobileLayout
      header={
        <AppToolbar
          title={t('extensions:bikeleasing_v2_benefit.infoPage.header')}
          backButton={<BackIconButton onClick={backNavigator.back} />}
        />
      }
      paperBackground
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginX: 1,
          gap: 0.8,
          marginBottom: 1.5,
        }}
      >
        <Box
          data-test-id="bike-info-image"
          component="img"
          src={BENEFIT_PICTURES.BIKE}
          sx={{
            borderRadius: '8px',
            width: '100%',
            maxWidth: 410,
          }}
        />
        <ExternalLinkButton href={informNowLink} fullWidth>
          {t('extensions:bikeleasing_v2.infoPage.informNow')}
        </ExternalLinkButton>
        <InfoText />
        <DesiredBike />
        <ReceiptBenefits />
        <HelpfulResources />
        <BikeleasingIcon width={'90%'} />
      </Box>
    </MobileLayout>
  );
};
